/** Poki-only visit analytics. Never persists player data or changes game state. */
export function pokiGameDay(cycleIndex) {
  var cycle = Number(cycleIndex);
  return "day-" + (Number.isFinite(cycle) ? Math.max(0, Math.floor(cycle)) + 1 : 1);
}

/** Night 1 is the merge phase following Day 1 of the current run. */
export function pokiGameNight(cycleIndex) {
  var cycle = Number(cycleIndex);
  return "night-" + (Number.isFinite(cycle) ? Math.max(0, Math.floor(cycle)) + 1 : 1);
}

const ACTIVE_SECONDS = [30, 60, 120, 180, 240, 300, 600];
const BOOSTERS = {
  free_shake: "shake-free", paid_shake: "shake-gems",
  free_bomb_cell: "clear-cell-free", paid_bomb_cell: "clear-cell-gems",
  free_bomb_triple: "clear-free", paid_bomb_triple: "clear-gems",
  free_minimize: "shrink-free", paid_minimize: "shrink-gems", paid_fix: "repair-gems",
};

export class PokiEngagement {
  constructor(send) {
    this.send = send;
    this.seen = new Set();
    this.milestones = new Set();
    this.audioBuckets = {};
    this.activeMs = 0;
    this.lastTime = null;
    this.active = false;
    this.timeCheckpoint = 0;
  }

  emit(category, what, action) {
    // Analytics must never interrupt gameplay, even with a failing SDK.
    try { this.send(category, what, action); } catch (_) { /* best effort */ }
  }

  once(category, what, action) {
    var key = category + ":" + what + ":" + action;
    if (this.milestones.has(key)) return;
    this.milestones.add(key);
    this.emit(category, what, action);
  }

  visible(category, what) {
    var key = category + ":" + what;
    if (this.seen.has(key)) return;
    this.seen.add(key);
    this.emit(category, what, "visible");
  }

  both(category, day, action) {
    this.emit(category, "all", action);
    this.emit(category, day, action);
  }

  visibleBoth(category, day) {
    this.visible(category, "all");
    this.visible(category, day);
  }

  retentionVisibility(view, context) {
    var day = pokiGameDay(context.cycle_index);
    if (view.wheelButton) this.visibleBoth("wheel-open", day);
    if (view.dailyButton) this.visible("button", "daily-reward");
    if (view.wheelButton && view.freeReady) this.visibleBoth("wheel-ready", day);
    if (view.dailyClaim) this.visibleBoth("daily-claim", day);
    if (view.freeSpin || view.adSpin) this.visibleBoth("wheel-spin", day);
    if (view.freeSpin) this.visibleBoth("wheel-spin-free", day);
    if (view.adSpin) {
      this.visibleBoth("wheel-spin-ad", day);
      this.visible("rewarded", "wheel-spin");
    }
  }

  activity(active, now) {
    if (!Number.isFinite(now)) return;
    if (this.lastTime != null && this.active) this.activeMs += Math.max(0, now - this.lastTime);
    this.lastTime = now;
    this.active = !!active;
    while (this.timeCheckpoint < ACTIVE_SECONDS.length
      && this.activeMs >= ACTIVE_SECONDS[this.timeCheckpoint] * 1000) {
      this.emit("active-play", String(ACTIVE_SECONDS[this.timeCheckpoint++]) + "s", "reached");
    }
  }

  track(name, context = {}) {
    var day = pokiGameDay(context.cycle_index);
    if (name === "wheel_opened") {
      this.both("wheel-open", day, "interact");
      if (context.free_ready) this.both("wheel-ready", day, "interact");
    } else if (name === "wheel_spin_free" || name === "wheel_spin_ad") {
      // Interactions count actual spins, not ad requests or cancelled videos.
      this.both("wheel-spin", day, "interact");
      this.both(name === "wheel_spin_free" ? "wheel-spin-free" : "wheel-spin-ad", day, "interact");
    } else if (name === "wheel_ad_requested") {
      this.emit("rewarded", "wheel-spin", "interact");
    } else if (name === "wheel_closed_without_spin") {
      this.both("wheel-exit", day, "without-spin");
    } else if (name === "wheel_reward_claimed") {
      this.both("wheel-reward", day, "claimed");
    } else if (name === "daily_opened") {
      this.both("daily-open", day, "opened");
      if (context.open_source === "hud") this.emit("button", "daily-reward", "interact");
      this.emit("daily-source", ["hud", "play_again", "return_visit"].includes(context.open_source)
        ? context.open_source.replaceAll("_", "-") : "other", "opened");
    } else if (name === "daily_claimed") {
      this.both("daily-claim", day, "interact");
    } else if (name === "drop_all") {
      // The game emits this once when a non-empty auto-pour actually starts.
      this.both("drop-all", pokiGameNight(context.cycle_index), "interact");
    } else if (name === "actual_catch" || name === "actual_merge") {
      this.once("onboarding", name === "actual_catch" ? "first-catch" : "first-merge", "reached");
    } else if (name === "fall_completed") {
      var caught = Number(context.caught_creatures);
      if (Number.isFinite(caught) && caught >= 0) {
        // The live SDK allows at most two numeric tokens per event. A numeric
        // range plus day-N contains three, so spell out the range endpoints.
        var bucket = caught === 0 ? "0" : caught <= 3 ? "one-to-three" : caught <= 7 ? "four-to-seven" : caught <= 12 ? "eight-to-twelve" : "13-plus";
        this.emit("catch-count", day, bucket);
      }
    } else if (name === "flask_broken" || name === "overflow") {
      this.both("loss", day, name === "overflow" ? "overflow" : "flask-broken");
    } else if (name === "revive_offered" || name === "revive_completed" || name === "revive_skipped") {
      this.both("revive", day, {revive_offered:"offered", revive_completed:"resumed", revive_skipped:"declined"}[name]);
    } else if (name === "play_again") {
      this.both("restart", day, "chosen");
    } else if (name === "run_start" || name === "run_resumed") {
      this.emit("run-entry", "all", name === "run_start" ? "new" : "resumed");
    } else if (name === "puzzle_piece_revealed") {
      var piece = Number(context.puzzle_piece);
      if (Number.isInteger(piece) && piece >= 0) this.emit("puzzle", "piece-" + piece, "revealed");
    } else if (name === "victory_shown" || name === "victory_continue") {
      this.emit("victory", "all", name === "victory_shown" ? "shown" : "continued");
    } else if (BOOSTERS[name]) {
      this.emit("booster", BOOSTERS[name], "used");
    } else if (name === "player_pause" || name === "player_resume") {
      this.both("player-pause", day, name === "player_pause" ? "paused" : "resumed");
    } else if (["music_off", "music_on", "sound_off", "sound_on"].includes(name)) {
      const [channel, state] = name.split("_");
      this.emit("audio", channel, state);
    } else if (name === "volume_changed") {
      const channel = context.channel === "music" ? "music" : "sound";
      const value = Math.max(0, Math.min(100, Number(context.value) || 0));
      const bucket = value === 0 ? "volume-muted" : value <= 33 ? "volume-low" : value <= 66 ? "volume-medium" : "volume-high";
      if (this.audioBuckets[channel] !== bucket) {
        this.audioBuckets[channel] = bucket;
        this.emit("audio", channel, bucket);
      }
    } else if (name === "gems_ad_opened" || name === "gems_ad_completed") {
      this.both("gems-reward", day, name === "gems_ad_opened" ? "opened" : "granted");
    } else if (name === "victory_feedback") {
      const value = Number(context.content_desire);
      if ([0, 5, 10].includes(value)) this.emit("victory-feedback", "all", value === 0 ? "no" : value === 5 ? "maybe" : "yes");
    }
  }
}
